// FP3-035A — Articulation Screener step (rendered inside
// AssessmentRunnerDialog when assessment_type === 'articulation_screener').
//
// Flow:
//   1. Provider picks a template (Ages 3-5 or Ages 5-8)
//   2. We create:
//        - a draft Assessment Run (stores items + scoring)
//        - a live screener session (token-bound for the Student Viewer)
//   3. Provider lands on the item-by-item scoring page:
//        - large picture prompt + target word + sound + position chip
//        - Correct / Incorrect / Not attempted scoring buttons
//        - optional error-type chips + notes
//        - Previous / Next nav, "Open Student Viewer" button
//        - live sound-level accuracy tile
//   4. Provider clicks "Complete screener" → flips the Assessment Run
//      to completed status (uses existing completeAssessmentRun API)
//      and ends the live viewer session.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Eye,
  Check,
  XCircle,
  MinusCircle,
  CaretLeft,
  CaretRight,
  Copy,
  ArrowSquareOut,
  Sparkle,
  Smiley,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const STATIC_URL = process.env.REACT_APP_BACKEND_URL;
const VIEWER_SYNC_DEBOUNCE_MS = 250;

const ERROR_TYPES = [
  { value: "substitution",        label: "Substitution" },
  { value: "omission",            label: "Omission" },
  { value: "distortion",          label: "Distortion" },
  { value: "frontal_distortion",  label: "Frontal distortion" },
  { value: "lateral_distortion",  label: "Lateral distortion" },
  { value: "other",               label: "Other" },
];

const POSITION_LABEL = {
  initial: "Initial",
  medial: "Medial",
  final: "Final",
  blend: "Blend",
};

export default function ArticulationScreenerStep({
  student,
  existingRun,
  onCompleted,
  onCancel,
}) {
  // Picker step → run step. If we already have a run id we skip the picker.
  const [phase, setPhase] = useState(existingRun ? "run" : "pick");
  const [templates, setTemplates] = useState([]);
  const [pickedTemplateId, setPickedTemplateId] = useState(
    existingRun?.screener_template_id || null,
  );
  const [template, setTemplate] = useState(null); // full template with items
  const [items, setItems] = useState([]);          // local scoring state
  const [currentIdx, setCurrentIdx] = useState(0);
  const [providerNotes, setProviderNotes] = useState("");
  const [clinicalSummary, setClinicalSummary] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [runId, setRunId] = useState(existingRun?.id || null);
  const [sessionToken, setSessionToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autosaveAt, setAutosaveAt] = useState(null);

  // ── Fetch templates on mount ───────────────────────────────────────
  useEffect(() => {
    if (phase !== "pick") return;
    (async () => {
      try {
        const r = await api.listScreenerTemplates();
        setTemplates(r.data.items || []);
      } catch (err) {
        toast.error("Failed to load screener templates");
      }
    })();
  }, [phase]);

  // ── Resume an existing draft ──────────────────────────────────────
  useEffect(() => {
    if (!existingRun) return;
    setItems(
      (existingRun.items || []).map((i) => ({
        id: i.id,
        prompt: i.prompt || "",
        score: typeof i.score === "number" ? i.score : null,
        notes: i.notes || "",
        target_word: i.target_word,
        target_sound: i.target_sound,
        word_position: i.word_position,
        sound_group: i.sound_group,
        image_url: i.image_url,
        error_type: i.error_type || null,
        substitution: i.substitution || "",
        visited: i.visited === true,
      })),
    );
    setProviderNotes(existingRun.provider_notes || "");
    setClinicalSummary(existingRun.clinical_summary || "");
    setRecommendations(existingRun.recommendations || "");
    setRunId(existingRun.id);
    setPickedTemplateId(existingRun.screener_template_id || null);
    setPhase("run");
  }, [existingRun]);

  // ── FP3-035B — Auto-visit + pre-select Correct on item display ────
  // When the provider opens the screener (or navigates to an item
  // they haven't seen yet), mark it visited and pre-select Correct.
  // Items never opened keep `visited=false, score=null` so they
  // don't count in the result panel.
  useEffect(() => {
    if (phase !== "run" || items.length === 0) return;
    const cur = items[currentIdx];
    if (!cur) return;
    if (!cur.visited) {
      setItems((arr) =>
        arr.map((it, i) =>
          i === currentIdx
            ? {
                ...it,
                visited: true,
                // Pre-select Correct on FIRST visit only — never overwrite
                // an explicit score the provider set previously.
                score: it.score === null || it.score === undefined ? 1 : it.score,
              }
            : it,
        ),
      );
    }
  }, [currentIdx, items.length, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Begin: provider picked a template → create run + live session ──
  const beginScreener = async (templateId) => {
    if (!templateId) {
      toast.error("Pick a screener first.");
      return;
    }
    setSaving(true);
    try {
      const tplResp = await api.getScreenerTemplate(templateId);
      const tpl = tplResp.data;
      setTemplate(tpl);
      const seededItems = (tpl.items || []).map((it) => ({
        id: null,
        prompt: `Say "${it.target_word}"`,
        score: null,
        notes: "",
        target_word: it.target_word,
        target_sound: it.target_sound,
        word_position: it.word_position,
        sound_group: it.sound_group,
        image_url: it.image_url,
        error_type: null,
        substitution: "",
        visited: false,
      }));
      // Create the Assessment Run as a draft (will be completed at end)
      const runPayload = {
        assessment_type: "articulation_screener",
        title: tpl.name,
        skill_area: "Speech — Articulation",
        date_administered: new Date().toISOString().slice(0, 10),
        timer_seconds: null,
        items: seededItems,
        provider_notes: "",
        clinical_summary: "",
        recommendations: "",
        screener_template_id: tpl.id,
      };
      const runResp = await api.createAssessmentRun(student.id, runPayload);
      const created = runResp.data;
      setRunId(created.id);
      setItems(
        (created.items || []).map((i) => ({
          id: i.id,
          prompt: i.prompt,
          score: typeof i.score === "number" ? i.score : null,
          notes: i.notes || "",
          target_word: i.target_word,
          target_sound: i.target_sound,
          word_position: i.word_position,
          sound_group: i.sound_group,
          image_url: i.image_url,
          error_type: i.error_type || null,
          substitution: i.substitution || "",
          visited: i.visited === true,
        })),
      );
      // Start the live viewer session
      const sessResp = await api.startScreenerSession({
        student_id: student.id,
        template_id: tpl.id,
        run_id: created.id,
      });
      setSessionToken(sessResp.data.token);
      setCurrentIdx(0);
      setPickedTemplateId(tpl.id);
      setPhase("run");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to start screener";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Push current item index to the viewer session (debounced) ─────
  const syncTimerRef = useRef(null);
  useEffect(() => {
    if (!sessionToken) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      api.patchScreenerSession(sessionToken, { current_item_index: currentIdx }).catch(() => {});
    }, VIEWER_SYNC_DEBOUNCE_MS);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [currentIdx, sessionToken]);

  // ── Autosave with a 1.5s debounce on item / notes changes ─────────
  // FP3-035B — extracts the wire-shape items array so save-draft +
  // complete + autosave all serialize visited + substitution alongside
  // score/error_type.
  const itemsToWire = () =>
    items.map((it) => ({
      id: it.id,
      prompt: it.prompt,
      score: typeof it.score === "number" ? it.score : null,
      notes: it.notes,
      target_word: it.target_word,
      target_sound: it.target_sound,
      word_position: it.word_position,
      sound_group: it.sound_group,
      image_url: it.image_url,
      error_type: it.error_type,
      substitution: it.substitution || null,
      visited: it.visited === true,
    }));
  const autosaveTimerRef = useRef(null);
  useEffect(() => {
    if (!runId || phase !== "run") return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const payload = {
          items: itemsToWire(),
          provider_notes: providerNotes,
          clinical_summary: clinicalSummary,
          recommendations,
        };
        await api.patchAssessmentRun(student.id, runId, payload);
        setAutosaveAt(new Date());
      } catch {
        // silent — the explicit Complete button surfaces errors
      }
    }, 1500);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [items, providerNotes, clinicalSummary, recommendations, runId, student.id, phase]);

  // ── Score / error helpers ──────────────────────────────────────────
  const setScore = (score) => {
    setItems((cur) =>
      cur.map((it, i) =>
        i === currentIdx
          ? {
              ...it,
              score,
              // Visiting an item via any score click guarantees we
              // never lose it in the summary even if the visited
              // effect lagged on first render.
              visited: true,
              // Clear error_type + substitution when the score flips
              // away from "incorrect" — those fields are meaningless
              // for Correct / Not Attempted.
              error_type: score === 0 ? it.error_type : null,
              substitution: score === 0 ? it.substitution : "",
            }
          : it,
      ),
    );
  };
  const setErrorType = (errorType) => {
    setItems((cur) =>
      cur.map((it, i) =>
        i === currentIdx
          ? {
              ...it,
              error_type: errorType,
              // Substitution only applies to error_type=substitution;
              // wipe it when the provider picks a different category.
              substitution: errorType === "substitution" ? it.substitution : "",
            }
          : it,
      ),
    );
  };
  const setSubstitution = (substitution) => {
    setItems((cur) =>
      cur.map((it, i) =>
        i === currentIdx ? { ...it, substitution } : it,
      ),
    );
  };
  const setItemNotes = (notes) => {
    setItems((cur) =>
      cur.map((it, i) => (i === currentIdx ? { ...it, notes } : it)),
    );
  };

  const goPrev = () => setCurrentIdx((i) => Math.max(0, i - 1));
  const goNext = () =>
    setCurrentIdx((i) => Math.min(items.length - 1, i + 1));

  // ── Live summary (overall + per-sound + per-position) ─────────────
  const summary = useMemo(() => {
    const scored = items.filter((i) => i.score === 0 || i.score === 1);
    const correct = scored.filter((i) => i.score === 1).length;
    const accuracy = scored.length
      ? Math.round((correct / scored.length) * 1000) / 10
      : null;
    // FP3-035B — "Not attempted" = visited but explicitly null.
    // "Unvisited" = visited=false; not shown in the live tile.
    const notAttempted = items.filter(
      (i) => i.visited === true && (i.score === null || i.score === undefined),
    ).length;
    const groupBy = (key) => {
      const buckets = new Map();
      items.forEach((it) => {
        const k = it[key];
        if (!k) return;
        const b = buckets.get(k) || { key: k, scored: 0, correct: 0 };
        if (it.score === 0 || it.score === 1) {
          b.scored += 1;
          if (it.score === 1) b.correct += 1;
        }
        buckets.set(k, b);
      });
      return [...buckets.values()].map((b) => ({
        ...b,
        accuracy: b.scored ? Math.round((b.correct / b.scored) * 1000) / 10 : null,
      }));
    };
    return {
      total: items.length,
      scored: scored.length,
      correct,
      incorrect: scored.length - correct,
      not_attempted: notAttempted,
      percent_accuracy: accuracy,
      by_sound: groupBy("target_sound"),
      by_position: groupBy("word_position"),
    };
  }, [items]);

  // ── Complete / end ─────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!runId) {
      toast.error("No run to complete.");
      return;
    }
    if (summary.scored === 0) {
      toast.error("Score at least one item before completing.");
      return;
    }
    setSaving(true);
    try {
      // Force-flush current edits before completing.
      const payload = {
        items: itemsToWire(),
        provider_notes: providerNotes,
        clinical_summary: clinicalSummary,
        recommendations,
      };
      await api.patchAssessmentRun(student.id, runId, payload);
      const completeResp = await api.completeAssessmentRun(student.id, runId);
      if (sessionToken) {
        await api.endScreenerSession(sessionToken).catch(() => {});
      }
      toast.success("Screener completed");
      onCompleted?.(completeResp.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to complete screener";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Save draft + close ─────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!runId) {
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      const payload = {
        items: itemsToWire(),
        provider_notes: providerNotes,
        clinical_summary: clinicalSummary,
        recommendations,
      };
      await api.patchAssessmentRun(student.id, runId, payload);
      if (sessionToken) {
        await api.endScreenerSession(sessionToken).catch(() => {});
      }
      toast.success("Draft saved");
      onCompleted?.({ id: runId, status: "draft" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Renderers ──────────────────────────────────────────────────────

  if (phase === "pick") {
    return (
      <div
        data-testid="screener-picker"
        className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4"
      >
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-[#1F2937]">
            Pick a screener
          </h3>
          <p className="text-xs text-[#6B7280]">
            Provider-authored articulation screeners. Items show a picture
            prompt + target word + target sound + word position. Score each
            item as Correct, Incorrect, or Not attempted; the app rolls up
            overall, per-sound, and per-position accuracy.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => {
            const active = pickedTemplateId === t.id;
            return (
              <button
                key={t.id}
                data-testid={`screener-template-${t.id}`}
                onClick={() => setPickedTemplateId(t.id)}
                className={`text-left rounded-xl border p-4 transition-colors ${
                  active
                    ? "border-[#1E3F33] bg-[#F1F7F2]"
                    : "border-[#E5E3DB] bg-white hover:border-[#9B9890]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
                      active
                        ? "bg-[#1E3F33] text-white"
                        : "bg-white text-[#1E3F33] border border-[#E5E3DB]"
                    }`}
                  >
                    <Smiley size={20} weight="duotone" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1F2937]">
                      {t.short_name}
                    </p>
                    <p className="text-[11px] uppercase tracking-wider text-[#6B7280] mt-0.5">
                      Age {t.age_range} · {t.item_count} items
                    </p>
                    <p className="text-xs text-[#4B5563] mt-2 leading-snug">
                      {t.description}
                    </p>
                  </div>
                  {active && (
                    <Check
                      size={18}
                      weight="bold"
                      className="text-[#1E3F33] mt-1 shrink-0"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-[#ECEAE4]">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="screener-pick-cancel"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-testid="screener-pick-begin"
            onClick={() => beginScreener(pickedTemplateId)}
            disabled={!pickedTemplateId || saving}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {saving ? "Starting…" : "Begin screener"}
          </Button>
        </div>
      </div>
    );
  }

  // RUN phase ─────────────────────────────────────────────────────────
  const cur = items[currentIdx];

  return (
    <div
      data-testid="screener-run"
      className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4"
    >
      {/* Header strip */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="screener-active-type"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-[#1E3F33] bg-[#E8F0EB] px-2 py-1 rounded-full"
          >
            <Smiley size={12} weight="fill" /> Articulation screener
          </span>
          {template && (
            <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">
              {template.short_name || template.name}
            </span>
          )}
          <span
            data-testid="screener-progress"
            className="text-[11px] uppercase tracking-wider text-[#6B7280]"
          >
            · Item {currentIdx + 1} of {items.length}
          </span>
        </div>
        <ViewerLaunchButton token={sessionToken} />
      </div>

      {/* Item viewer + scoring */}
      {cur && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Picture + prompt card */}
          <div
            data-testid="screener-item-card"
            className="lg:col-span-7 rounded-2xl border border-[#ECEAE4] bg-white p-4 sm:p-5 flex flex-col items-center"
          >
            {/* Stable bounded image container — fixed aspect ratio,
                white background, generous padding. object-contain
                keeps every page render visible without clipping
                regardless of source page dimensions. */}
            <div
              data-testid="screener-item-image-frame"
              className="w-full max-w-[420px] aspect-[4/5] bg-[#FBFAF6] rounded-xl border border-[#ECEAE4] flex items-center justify-center p-3 mb-4 overflow-hidden"
            >
              {cur.image_url ? (
                <img
                  data-testid="screener-item-image"
                  src={`${STATIC_URL}${cur.image_url}`}
                  alt={cur.target_word || ""}
                  draggable={false}
                  className="max-w-full max-h-full object-contain select-none"
                  style={{ display: "block" }}
                />
              ) : (
                <span className="text-xs text-[#6B7280]">No image</span>
              )}
            </div>
            <p
              data-testid="screener-item-target-word"
              className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F2937]"
              style={{ fontFamily: '"Manrope", system-ui, sans-serif' }}
            >
              {cur.target_word}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
              <span
                data-testid="screener-item-sound-chip"
                className="text-xs uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full bg-[#E8F0EB] text-[#1E3F33]"
              >
                Sound {cur.target_sound}
              </span>
              <span
                data-testid="screener-item-position-chip"
                className="text-xs uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full bg-[#FDF4E5] text-[#9C6B2E]"
              >
                {POSITION_LABEL[cur.word_position] || cur.word_position}
              </span>
              {cur.sound_group && (
                <span className="text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-0.5 rounded-full border border-[#E5E3DB]">
                  {cur.sound_group}
                </span>
              )}
            </div>
            <p className="text-[11px] text-[#6B7280] mt-3 italic">
              Provider prompt: {cur.prompt}
            </p>
          </div>

          {/* Scoring controls + notes */}
          <div className="lg:col-span-5 space-y-3">
            <div className="rounded-2xl border border-[#ECEAE4] bg-white p-4 space-y-3">
              <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563]">
                Production score
              </Label>
              <div className="grid grid-cols-3 gap-2">
                <ScoreButton
                  testId="screener-score-correct"
                  active={cur.score === 1}
                  onClick={() => setScore(1)}
                  tone="green"
                  icon={Check}
                  label="Correct"
                />
                <ScoreButton
                  testId="screener-score-incorrect"
                  active={cur.score === 0}
                  onClick={() => setScore(0)}
                  tone="red"
                  icon={XCircle}
                  label="Incorrect"
                />
                <ScoreButton
                  testId="screener-score-not-attempted"
                  active={cur.score === null}
                  onClick={() => setScore(null)}
                  tone="grey"
                  icon={MinusCircle}
                  label="Not attempted"
                />
              </div>
            </div>

            {cur.score === 0 && (
              <div className="rounded-2xl border border-[#F1E1D9] bg-[#FFF7F3] p-4 space-y-2">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#7A3A2C]">
                  Error type (optional)
                </Label>
                <div className="flex flex-wrap gap-2">
                  {ERROR_TYPES.map((e) => {
                    const active = cur.error_type === e.value;
                    return (
                      <button
                        key={e.value}
                        type="button"
                        data-testid={`screener-error-${e.value}`}
                        onClick={() => setErrorType(active ? null : e.value)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                          active
                            ? "bg-[#7A3A2C] text-white border-[#7A3A2C]"
                            : "bg-white text-[#7A3A2C] border-[#7A3A2C]/30 hover:bg-[#F8E8E2]"
                        }`}
                      >
                        {e.label}
                      </button>
                    );
                  })}
                </div>
                {/* FP3-035B — Substitution input. Appears only when
                    error_type=substitution. The phoneme the student
                    actually produced; we render it in result lists
                    as e.g. "/t/ for /k/". */}
                {cur.error_type === "substitution" && (
                  <div className="pt-2 space-y-1">
                    <Label className="text-[10px] uppercase tracking-wide font-semibold text-[#7A3A2C]">
                      Substitution produced
                    </Label>
                    <Input
                      data-testid="screener-substitution"
                      value={cur.substitution || ""}
                      onChange={(e) => setSubstitution(e.target.value)}
                      placeholder="e.g. /t/, /w/, /b/"
                      className="h-8 text-xs"
                    />
                    <p className="text-[10px] text-[#9B6753] italic">
                      Renders in results as "{cur.substitution || "/?/"} for {cur.target_sound}"
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-[#ECEAE4] bg-white p-4 space-y-2">
              <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563]">
                Item notes (optional)
              </Label>
              <Textarea
                data-testid="screener-item-notes"
                value={cur.notes || ""}
                onChange={(e) => setItemNotes(e.target.value)}
                placeholder="e.g. produced w/ visual cue, stopped /f/ to /p/"
                className="text-xs min-h-[64px]"
              />
            </div>

            {/* Prev / Next */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="screener-prev"
                onClick={goPrev}
                disabled={currentIdx === 0}
                className="text-xs"
              >
                <CaretLeft size={13} className="mr-1" /> Prev
              </Button>
              <span className="text-[11px] text-[#6B7280] tabular-nums">
                {currentIdx + 1} / {items.length}
              </span>
              <Button
                size="sm"
                data-testid="screener-next"
                onClick={goNext}
                disabled={currentIdx === items.length - 1}
                className="text-xs bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                Next <CaretRight size={13} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Live summary */}
      <div
        data-testid="screener-summary"
        className="rounded-2xl border border-[#ECEAE4] bg-[#F9F8F6] p-4 space-y-3"
      >
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <SummaryStat label="Items" value={summary.total} />
          <SummaryStat label="Scored" value={summary.scored} />
          <SummaryStat label="Correct" value={summary.correct} />
          <SummaryStat label="Incorrect" value={summary.incorrect} />
          <SummaryStat label="Not attempted" value={summary.not_attempted} />
          <SummaryStat
            label="Accuracy"
            value={
              summary.percent_accuracy == null
                ? "—"
                : `${summary.percent_accuracy}%`
            }
            accent
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-[#ECEAE4]">
          <RollupTable
            testId="screener-rollup-by-sound"
            title="By sound"
            buckets={summary.by_sound}
          />
          <RollupTable
            testId="screener-rollup-by-position"
            title="By word position"
            buckets={summary.by_position}
            labelMap={POSITION_LABEL}
          />
        </div>
      </div>

      {/* Notes / clinical / recs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
            Provider notes
          </Label>
          <Textarea
            data-testid="screener-provider-notes"
            value={providerNotes}
            onChange={(e) => setProviderNotes(e.target.value)}
            placeholder="Observations during administration — student behavior, prompts given, etc."
            className="text-xs min-h-[72px]"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
            Clinical summary
          </Label>
          <Textarea
            data-testid="screener-clinical-summary"
            value={clinicalSummary}
            onChange={(e) => setClinicalSummary(e.target.value)}
            placeholder="What did this screener tell you?"
            className="text-xs min-h-[72px]"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
            Recommendations / next steps
          </Label>
          <Textarea
            data-testid="screener-recommendations"
            value={recommendations}
            onChange={(e) => setRecommendations(e.target.value)}
            placeholder="Suggested follow-up — target updates, additional probes, instructional shifts."
            className="text-xs min-h-[60px]"
          />
        </div>
      </div>

      {/* Actions footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#ECEAE4]">
        <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
          {autosaveAt && (
            <span data-testid="screener-autosave">
              <Sparkle size={11} className="inline mr-1" />
              Saved {autosaveAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving}
            data-testid="screener-save-draft"
          >
            Save draft & close
          </Button>
          <Button
            onClick={handleComplete}
            disabled={saving || summary.scored === 0}
            data-testid="screener-complete"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {saving ? "Saving…" : "Complete screener"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ScoreButton({ testId, active, onClick, tone, icon: Icon, label }) {
  const palette = {
    green: { idle: "text-[#1E3F33] border-[#1E3F33]/30 hover:bg-[#E8F0EB]", active: "bg-[#1E3F33] text-white border-[#1E3F33]" },
    red:   { idle: "text-[#7A3A2C] border-[#7A3A2C]/30 hover:bg-[#F8E8E2]", active: "bg-[#7A3A2C] text-white border-[#7A3A2C]" },
    grey:  { idle: "text-[#4B5563] border-[#9B9890]/40 hover:bg-[#F1EFE8]",  active: "bg-[#4B5563] text-white border-[#4B5563]" },
  }[tone];
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`h-12 rounded-xl border font-semibold text-sm transition inline-flex items-center justify-center gap-1.5 ${
        active ? palette.active : `bg-white ${palette.idle}`
      }`}
    >
      <Icon size={16} weight={active ? "fill" : "regular"} />
      {label}
    </button>
  );
}

function SummaryStat({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">
        {label}
      </p>
      <p
        className={`text-base font-semibold tabular-nums ${
          accent ? "text-[#1E3F33]" : "text-[#1F2937]"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

function RollupTable({ testId, title, buckets, labelMap }) {
  if (!buckets || buckets.length === 0) {
    return (
      <div data-testid={testId}>
        <p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
          {title}
        </p>
        <p className="text-[11px] text-[#9B9890] italic">No scored items yet.</p>
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
        {title}
      </p>
      <ul className="space-y-1">
        {buckets.map((b) => {
          const label = labelMap?.[b.key] || b.key;
          return (
            <li
              key={b.key}
              className="flex items-center justify-between text-[11px] gap-2 px-2 py-1 rounded bg-white border border-[#ECEAE4]"
            >
              <span className="font-mono text-[#1F2937]">{label}</span>
              <span className="text-[#6B7280]">
                {b.correct}/{b.scored}{" "}
                <span className="font-semibold text-[#1E3F33]">
                  {b.accuracy == null ? "—" : `${b.accuracy}%`}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ViewerLaunchButton({ token }) {
  if (!token) return null;
  const viewerUrl = `${window.location.origin}/student-viewer/${token}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(viewerUrl);
      toast.success("Student Viewer URL copied");
    } catch {
      toast.error("Couldn't copy URL — open in a new tab instead");
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid="screener-viewer-copy"
        onClick={copy}
        className="h-8 text-xs"
      >
        <Copy size={12} className="mr-1" /> Copy viewer URL
      </Button>
      <Button
        type="button"
        size="sm"
        data-testid="screener-viewer-open"
        onClick={() => window.open(viewerUrl, "_blank", "noopener,noreferrer")}
        className="h-8 text-xs bg-[#1E3F33] hover:bg-[#173127] text-white"
      >
        <Eye size={12} weight="fill" className="mr-1" />
        Open student viewer <ArrowSquareOut size={11} className="ml-1" />
      </Button>
    </div>
  );
}
